import type { Notification } from '../api/DigiobcanApi';
import DigiobcanApi from '../api/DigiobcanApi';
import { meGetNotifications } from '../features/queries/meGetNotifications';
import { meGetNotificationsUnreadCount } from '../features/queries/meGetNotificationsUnreadCount';
import { useInfiniteQ, useM, useQ } from '../lib/query';
import { useLogger } from './LoggerProvider';
import { useMqtt } from './MqttClientProvider';
import { useAuth } from '@presov.sk/presov-auth-react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import React from 'react';

type NotificationsPage = {
  notifications: Notification[];
  hasNextOffset: boolean;
};

type NotificationsInfiniteData = InfiniteData<NotificationsPage>;

type NotificationsContextType = {
  notifications: Notification[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  unreadCount: number;
  fetchMore: () => Promise<void>;
  fetchInitial: () => void;
  markNotificationsAsReadMutation: ReturnType<typeof useM>;
};

const NotificationsContext = React.createContext<NotificationsContextType>({
  notifications: [],
  isLoading: false,
  isLoadingMore: false,
  hasMore: true,
  unreadCount: 0,
  fetchMore: async () => {},
  fetchInitial: async () => {},
  markNotificationsAsReadMutation: undefined,
});

const LIMIT = 15;

type Props = React.PropsWithChildren;

const NotificationsProvider: React.FC<Props> = ({ children }) => {
  const { logger } = useLogger();
  const queryClient = useQueryClient();
  const { authUser, authState } = useAuth();
  const { subscribe } = useMqtt();

  const [enabled, setEnabled] = React.useState(false);

  const {
    data = { pages: [] },
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    queryKey,
  } = useInfiniteQ(
    meGetNotifications,
    {
      limit: LIMIT,
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (!lastPage.hasNextOffset) {
          return undefined;
        }
        const lastItem =
          lastPage.notifications[lastPage.notifications.length - 1];
        return [lastItem.sent_at, lastItem.tag];
      },
      enabled,
    },
  );

  const allNotifications = Array.from(
    data.pages
      .flatMap((page) => page.notifications)
      .reduce((map: Map<string, Notification>, notif: Notification) => {
        map.set(notif.tag, notif);
        return map;
      }, new Map<string, Notification>())
      .values(),
  ) as Notification[];

  const fetchInitial = () => {
    setEnabled(true);
  };

  const { data: unreadCount = 0, queryKey: unreadCountQueryKey } = useQ(
    meGetNotificationsUnreadCount,
    undefined,
    {
      enabled: authState === 'authorized',
    },
  );

  const markNotificationsAsReadMutation = useM(
    (notificationTags: string[]) => {
      return DigiobcanApi.meNotificationsMarkAsRead(undefined, {
        notificationTags,
      });
    },
    {
      onSuccess: ({ unread_count, notifications: updated }) => {
        const updatedMap = new Map(
          updated.map((notification) => [notification.tag, notification]),
        );

        queryClient.setQueryData(
          queryKey,
          (oldData: NotificationsInfiniteData | undefined) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                notifications: page.notifications.map((notification) => {
                  if (updatedMap.has(notification.tag)) {
                    return updatedMap.get(notification.tag);
                  } else {
                    return notification;
                  }
                }),
              })),
            };
          },
        );

        queryClient.setQueryData(unreadCountQueryKey, unread_count);
      },
      onError: (error, variables) => {
        logger.error('Error marking notifications as read', {
          action: 'markNotificationsAsRead',
          error,
          data: { notificationTags: variables },
        });
      },
    },
  );

  React.useEffect(() => {
    if (authUser?.clientAccountId) {
      subscribe(
        `digiobcan/user/${authUser.clientAccountId}/notifications`,
        (message) => {
          const notification = JSON.parse(message) as Notification;

          if (data.pages.length >= 1) {
            queryClient.setQueryData(
              queryKey,
              (oldData: NotificationsInfiniteData | undefined) => {
                if (!oldData) return oldData;
                const newPages = [...oldData.pages];
                newPages[0] = {
                  ...newPages[0],
                  notifications: [notification, ...newPages[0].notifications],
                };

                return {
                  ...oldData,
                  pages: newPages,
                };
              },
            );
          }

          queryClient.setQueryData(
            unreadCountQueryKey,
            (prev: number = 0) => prev + 1,
          );
        },
        { qos: 1 },
      );
    }

    return () => {};
  }, [
    subscribe,
    authUser,
    queryClient,
    queryKey,
    unreadCountQueryKey,
    data.pages.length,
  ]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications: allNotifications,
        isLoading,
        isLoadingMore: isFetchingNextPage,
        hasMore: hasNextPage,
        unreadCount,
        fetchMore: async () => {
          await fetchNextPage();
        },
        fetchInitial,
        markNotificationsAsReadMutation,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export default NotificationsProvider;

export const useNotifications = () => {
  const context = React.useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationsContext',
    );
  }
  return context;
};
